type UITranslationKey =
  | "add"
  | "and"
  | "file"
  | "files"
  | "next"
  | "submit"
  | "submitAndSign"
  | "showSections"
  | "sections"
  | "summary"
  | "reviewResponses"
  | "editSection"
  | "indicatesRequiredInformation"
  | "confirm"
  | "cancel"
  | "autofillConfirmMessage1"
  | "autofillConfirmMessage2"
  | "autofillFailedMessage"
  | "appointmentSelectAttendee"
  | "appointmentAvailableAttendees"
  | "appointmentNoAttendeesAvailable"
  | "appointmentFirstAvailable"
  | "appointmentInPersonMeeting"
  | "appointmentPhoneMeeting"
  | "appointmentTimezone"
  | "appointmentNoOptions"
  | "appointmentTime"
  | "appointmentClearSelection"
  | "appointmentNoAppointmentMade"
  | "appointmentSelectedAttendee"
  | "appointmentDate"
  | "appointmentMonth"
  | "appointmentYear"
  | "appointmentPreviousMonth"
  | "appointmentNextMonth"
  | "appointmentSelectedMessage"
  | "appointmentNotSelectedMessage"
  | "minute"
  | "minutes"
  | "hour"
  | "hours"
  | "formCompletedOn"
  | "languageBannerAbove"
  | "languageBannerBelow"
  | "connectionSaveFailed"
  | "connectionAutoSaveMessage"
  | "connectionTryAgain"
  | "delete"
  | "other"
  | "otherValue"
  | "paymentProcessedOnSubmit"
  | "errorSummaryHeading"
  | "goToFirstError"
  | "requiredQuestion"
  | "pleaseEnterValidResponse"
  | "pleaseEnterValidDate"
  | "pleaseEnterValidEmail"
  | "filesFailedToUpload"
  | "filesStillUploading"
  | "pleaseEnterValidPhone"
  | "appointmentMustBeCompleted"
  | "paymentMustBeCompleted"
  | "pleaseSelectOptionsThatApply"
  | "pleaseMakeASelection"
  | "saveForm"
  | "formHasBeenCompleted"
  | "formHasBeenClosed"
  | "formNotCurrentlyAvailable"
  | "formHasBeenRemoved"
  | "formStatusMessage"
  | "contactFallbackUs"
  | "signature"
  | "signatureRequired"
  | "signatureDraw"
  | "signatureType"
  | "signatureClear"
  | "signatureEditingDisabled"
  | "digitalSignatureCalloutBody"
  | "continueLabel"
  | "signatureModalRedirect"
  | "signatureModalContact";

export type SupportedLanguageKey =
  | "ar"
  | "zh"
  | "fr"
  | "de"
  | "it"
  | "ja"
  | "ko"
  | "pl"
  | "pt"
  | "ru"
  | "es"
  | "vi";

type UITranslations = Record<
  SupportedLanguageKey | "en",
  Record<UITranslationKey, string>
>;

export const uiTranslations: UITranslations = {
  en: {
    add: "Add",
    and: "and",
    file: "file",
    files: "files",
    next: "Next",
    submit: "Submit",
    submitAndSign: "Submit & Sign",
    showSections: "Show Sections",
    sections: "Sections",
    summary: "Summary",
    reviewResponses: "Please review your responses",
    editSection: "Edit section",
    indicatesRequiredInformation: "Indicates required information",
    confirm: "Confirm",
    cancel: "Cancel",
    autofillConfirmMessage1:
      "Before clicking 'Confirm', ensure all relevant documents are uploaded.",
    autofillConfirmMessage2:
      "Once confirmed, we will auto-populate the form, and no further uploads or use of the 'Autofill Assistant' will be possible.",
    autofillFailedMessage:
      "We couldn't read the documents you uploaded. Please fill in the form below.",
    appointmentSelectAttendee: "Select attendee",
    appointmentAvailableAttendees: "Available attendees",
    appointmentNoAttendeesAvailable: "No attendees available",
    appointmentFirstAvailable: "First Available",
    appointmentInPersonMeeting: "In-person meeting",
    appointmentPhoneMeeting: "Phone meeting",
    appointmentTimezone: "Timezone",
    appointmentNoOptions: "No options",
    appointmentTime: "Time",
    appointmentClearSelection: "Clear selection",
    appointmentNoAppointmentMade: "No appointment made",
    appointmentSelectedAttendee: "Selected attendee",
    appointmentDate: "Date",
    appointmentMonth: "Month",
    appointmentYear: "Year",
    appointmentPreviousMonth: "Previous month",
    appointmentNextMonth: "Next month",
    appointmentSelectedMessage:
      "You have selected {date} at {time} for your meeting",
    appointmentNotSelectedMessage:
      "A meeting will not be scheduled as you have not selected a time",
    minute: "minute",
    minutes: "minutes",
    hour: "hour",
    hours: "hours",
    formCompletedOn: "Form completed on",
    languageBannerAbove:
      "You can change the language of this form at any time using the dropdown selector above",
    languageBannerBelow:
      "You can change the language of this form at any time using the dropdown selector below",
    connectionSaveFailed: "Failed to save your form.",
    connectionAutoSaveMessage:
      "Your changes will be saved automatically as you complete the form.",
    connectionTryAgain: "Try again",
    delete: "Delete",
    other: "Other",
    otherValue: "Other Value",
    paymentProcessedOnSubmit:
      "Payment will be processed upon submission of this form.",
    errorSummaryHeading: "Please correct the following {count} errors:",
    goToFirstError: "Go to first error",
    requiredQuestion: "This is a required question",
    pleaseEnterValidResponse: "Please enter a valid response",
    pleaseEnterValidDate: "Please enter a valid date",
    pleaseEnterValidEmail: "Please enter a valid email address",
    filesFailedToUpload: "One or more files failed to upload.",
    filesStillUploading: "Some files are still being uploaded.",
    pleaseEnterValidPhone: "Please enter a valid phone number",
    appointmentMustBeCompleted: "Appointment field must be completed",
    paymentMustBeCompleted: "Payment must be completed",
    pleaseSelectOptionsThatApply: "Please select all options that apply",
    pleaseMakeASelection: "Please make a selection",
    saveForm: "Save Form",
    formHasBeenCompleted: "has been completed",
    formHasBeenClosed: "has been closed",
    formNotCurrentlyAvailable: "is not currently available",
    formHasBeenRemoved: "has been removed",
    formStatusMessage:
      "This form {status}. Please contact {contact} if you have any further questions.",
    contactFallbackUs: "us",
    signature: "Signature",
    signatureRequired: "Signature required",
    signatureDraw: "Draw",
    signatureType: "Type",
    signatureClear: "Clear",
    signatureEditingDisabled: "Signature editing is disabled",
    digitalSignatureCalloutBody:
      "When you {action}, you'll be taken to a secure {provider} page to apply your signature.",
    continueLabel: "Continue",
    signatureModalRedirect:
      "This form requires a signature, select {action} to be redirected to the secure signing page",
    signatureModalContact:
      "This form requires a signature. Please contact {contact} if you have any further questions.",
  },
  ar: {
    add: "إضافة",
    and: "و",
    file: "الملف",
    files: "الملفات",
    next: "التالي",
    submit: "إرسال",
    submitAndSign: "إرسال وتوقيع",
    showSections: "عرض الأقسام",
    sections: "الأقسام",
    summary: "ملخص",
    reviewResponses: "يرجى مراجعة إجاباتك",
    editSection: "تعديل القسم",
    indicatesRequiredInformation: "يشير إلى المعلومات المطلوبة",
    confirm: "تأكيد",
    cancel: "إلغاء",
    autofillConfirmMessage1:
      "قبل النقر على 'تأكيد'، تأكد من تحميل جميع المستندات ذات الصلة.",
    autofillConfirmMessage2:
      "بمجرد التأكيد، سنقوم بملء النموذج تلقائيًا، ولن يكون من الممكن إجراء المزيد من التحميلات أو استخدام 'مساعد الملء التلقائي'.",
    autofillFailedMessage:
      "لم نتمكن من قراءة المستندات التي قمت بتحميلها. يرجى تعبئة النموذج أدناه.",
    appointmentSelectAttendee: "اختر الحضور",
    appointmentAvailableAttendees: "الحضور المتاحون",
    appointmentNoAttendeesAvailable: "لا يوجد حضور متاحون",
    appointmentFirstAvailable: "أول متاح",
    appointmentInPersonMeeting: "اجتماع شخصي",
    appointmentPhoneMeeting: "اجتماع هاتفي",
    appointmentTimezone: "المنطقة الزمنية",
    appointmentNoOptions: "لا توجد خيارات",
    appointmentTime: "الوقت",
    appointmentClearSelection: "مسح الاختيار",
    appointmentNoAppointmentMade: "لم يتم تحديد موعد",
    appointmentSelectedAttendee: "الحضور المختار",
    appointmentDate: "التاريخ",
    appointmentMonth: "الشهر",
    appointmentYear: "السنة",
    appointmentPreviousMonth: "الشهر السابق",
    appointmentNextMonth: "الشهر التالي",
    appointmentSelectedMessage: "لقد اخترت {date} في {time} لاجتماعك",
    appointmentNotSelectedMessage: "لن يتم جدولة اجتماع لأنك لم تحدد وقتًا",
    minute: "دقيقة",
    minutes: "دقائق",
    hour: "ساعة",
    hours: "ساعات",
    formCompletedOn: "تم إكمال النموذج في",
    languageBannerAbove:
      "يمكنك تغيير لغة هذا النموذج في أي وقت باستخدام محدد القائمة المنسدلة أعلاه",
    languageBannerBelow:
      "يمكنك تغيير لغة هذا النموذج في أي وقت باستخدام محدد القائمة المنسدلة أدناه",
    connectionSaveFailed: "فشل في حفظ النموذج.",
    connectionAutoSaveMessage:
      "سيتم حفظ تغييراتك تلقائيًا أثناء إكمال النموذج.",
    connectionTryAgain: "حاول مرة أخرى",
    delete: "حذف",
    other: "آخر",
    otherValue: "قيمة أخرى",
    paymentProcessedOnSubmit: "سيتم معالجة الدفع عند إرسال هذا النموذج.",
    errorSummaryHeading: "يرجى تصحيح الأخطاء التالية ({count}):",
    goToFirstError: "الانتقال إلى الخطأ الأول",
    requiredQuestion: "هذا السؤال مطلوب",
    pleaseEnterValidResponse: "يرجى إدخال رد صالح",
    pleaseEnterValidDate: "يرجى إدخال تاريخ صالح",
    pleaseEnterValidEmail: "يرجى إدخال عنوان بريد إلكتروني صالح",
    filesFailedToUpload: "فشل تحميل ملف واحد أو أكثر.",
    filesStillUploading: "لا يزال يتم تحميل بعض الملفات.",
    pleaseEnterValidPhone: "يرجى إدخال رقم هاتف صالح",
    appointmentMustBeCompleted: "يجب إكمال حقل الموعد",
    paymentMustBeCompleted: "يجب إكمال الدفع",
    pleaseSelectOptionsThatApply: "يرجى تحديد جميع الخيارات المناسبة",
    pleaseMakeASelection: "يرجى إجراء تحديد",
    saveForm: "حفظ النموذج",
    formHasBeenCompleted: "تم إكماله",
    formHasBeenClosed: "تم إغلاقه",
    formNotCurrentlyAvailable: "غير متاح حاليًا",
    formHasBeenRemoved: "تمت إزالته",
    formStatusMessage:
      "هذا النموذج {status}. يرجى التواصل مع {contact} إذا كانت لديك أي أسئلة أخرى.",
    contactFallbackUs: "فريقنا",
    signature: "التوقيع",
    signatureRequired: "التوقيع مطلوب",
    signatureDraw: "رسم",
    signatureType: "كتابة",
    signatureClear: "مسح",
    signatureEditingDisabled: "تعديل التوقيع معطل",
    digitalSignatureCalloutBody:
      "عند اختيار {action}، سيتم نقلك إلى صفحة {provider} آمنة لإضافة توقيعك.",
    continueLabel: "متابعة",
    signatureModalRedirect:
      "يتطلب هذا النموذج توقيعًا، اختر {action} لتتم إعادة توجيهك إلى صفحة التوقيع الآمنة",
    signatureModalContact:
      "يتطلب هذا النموذج توقيعًا. يرجى التواصل مع {contact} إذا كانت لديك أي أسئلة أخرى.",
  },
  zh: {
    add: "添加",
    and: "和",
    file: "文件",
    files: "文件",
    next: "下一步",
    submit: "提交",
    submitAndSign: "提交并签名",
    showSections: "显示部分列表",
    sections: "部分列表",
    summary: "总览",
    reviewResponses: "请查看您的回复",
    editSection: "编辑该部分",
    indicatesRequiredInformation: "表示必填信息",
    confirm: "确认",
    cancel: "取消",
    autofillConfirmMessage1: "在点击'确认'之前，请确保已上传所有相关文档。",
    autofillConfirmMessage2:
      "确认后，我们将自动填写表单，届时将无法再上传文件或使用'自动填写助手'。",
    autofillFailedMessage: "我们无法读取您上传的文档。请在下方填写表单。",
    appointmentSelectAttendee: "选择参会人",
    appointmentAvailableAttendees: "可选参会人",
    appointmentNoAttendeesAvailable: "没有可选参会人",
    appointmentFirstAvailable: "最早可用",
    appointmentInPersonMeeting: "面对面会议",
    appointmentPhoneMeeting: "电话会议",
    appointmentTimezone: "时区",
    appointmentNoOptions: "无选项",
    appointmentTime: "时间",
    appointmentClearSelection: "清除选择",
    appointmentNoAppointmentMade: "未预约",
    appointmentSelectedAttendee: "已选参会人",
    appointmentDate: "日期",
    appointmentMonth: "月",
    appointmentYear: "年",
    appointmentPreviousMonth: "上个月",
    appointmentNextMonth: "下个月",
    appointmentSelectedMessage: "您已选择 {date} {time} 的会议",
    appointmentNotSelectedMessage: "由于您未选择时间，会议将不会被安排",
    minute: "分钟",
    minutes: "分钟",
    hour: "小时",
    hours: "小时",
    formCompletedOn: "表格完成于",
    languageBannerAbove: "您可随时使用上方的下拉选择器更改此表格的语言",
    languageBannerBelow: "您可随时使用下方的下拉选择器更改此表格的语言",
    connectionSaveFailed: "无法保存您的表格。",
    connectionAutoSaveMessage: "填写表格时，您的更改将自动保存。",
    connectionTryAgain: "重试",
    delete: "删除",
    other: "其他",
    otherValue: "其他值",
    paymentProcessedOnSubmit: "提交此表单后将处理付款。",
    errorSummaryHeading: "请更正以下 {count} 项错误：",
    goToFirstError: "转到第一个错误",
    requiredQuestion: "此为必填项",
    pleaseEnterValidResponse: "请输入有效回复",
    pleaseEnterValidDate: "请输入有效日期",
    pleaseEnterValidEmail: "请输入有效的电子邮件地址",
    filesFailedToUpload: "一个或多个文件上传失败。",
    filesStillUploading: "部分文件仍在上传中。",
    pleaseEnterValidPhone: "请输入有效的电话号码",
    appointmentMustBeCompleted: "必须完成预约",
    paymentMustBeCompleted: "必须完成付款",
    pleaseSelectOptionsThatApply: "请选择所有适用的选项",
    pleaseMakeASelection: "请进行选择",
    saveForm: "保存表格",
    formHasBeenCompleted: "已完成",
    formHasBeenClosed: "已关闭",
    formNotCurrentlyAvailable: "目前不可用",
    formHasBeenRemoved: "已被删除",
    formStatusMessage: "此表单{status}。如有任何其他疑问，请联系{contact}。",
    contactFallbackUs: "我们",
    signature: "签名",
    signatureRequired: "需要签名",
    signatureDraw: "手写",
    signatureType: "输入",
    signatureClear: "清除",
    signatureEditingDisabled: "签名编辑已禁用",
    digitalSignatureCalloutBody:
      "当您选择{action}时，系统会将您转至安全的{provider}页面以完成签名。",
    continueLabel: "继续",
    signatureModalRedirect:
      "此表单需要签名，请选择{action}以转至安全的签名页面",
    signatureModalContact:
      "此表单需要签名。如有任何其他疑问，请联系{contact}。",
  },
  fr: {
    add: "Ajouter",
    and: "et",
    file: "fichier",
    files: "fichiers",
    next: "Suivant",
    submit: "Soumettre",
    submitAndSign: "Soumettre et signer",
    showSections: "Afficher les sections",
    sections: "Sections",
    summary: "Résumé",
    reviewResponses: "Veuillez examiner vos réponses",
    editSection: "Modifier la section",
    indicatesRequiredInformation: "Indique les informations requises",
    confirm: "Confirmer",
    cancel: "Annuler",
    autofillConfirmMessage1:
      "Avant de cliquer sur 'Confirmer', assurez-vous que tous les documents pertinents sont téléchargés.",
    autofillConfirmMessage2:
      "Une fois confirmé, nous remplirons automatiquement le formulaire et aucun autre téléchargement ni utilisation de l'Assistant de remplissage automatique ne sera possible.",
    autofillFailedMessage:
      "Nous n'avons pas pu lire les documents que vous avez téléchargés. Veuillez remplir le formulaire ci-dessous.",
    appointmentSelectAttendee: "Sélectionner un participant",
    appointmentAvailableAttendees: "Participants disponibles",
    appointmentNoAttendeesAvailable: "Aucun participant disponible",
    appointmentFirstAvailable: "Premier disponible",
    appointmentInPersonMeeting: "Réunion en personne",
    appointmentPhoneMeeting: "Réunion téléphonique",
    appointmentTimezone: "Fuseau horaire",
    appointmentNoOptions: "Aucune option",
    appointmentTime: "Heure",
    appointmentClearSelection: "Effacer la sélection",
    appointmentNoAppointmentMade: "Aucun rendez-vous pris",
    appointmentSelectedAttendee: "Participant sélectionné",
    appointmentDate: "Date",
    appointmentMonth: "Mois",
    appointmentYear: "Année",
    appointmentPreviousMonth: "Mois précédent",
    appointmentNextMonth: "Mois suivant",
    appointmentSelectedMessage:
      "Vous avez sélectionné le {date} à {time} pour votre réunion",
    appointmentNotSelectedMessage:
      "Aucune réunion ne sera planifiée car vous n'avez pas sélectionné d'heure",
    minute: "minute",
    minutes: "minutes",
    hour: "heure",
    hours: "heures",
    formCompletedOn: "Formulaire complété le",
    languageBannerAbove:
      "Vous pouvez changer la langue de ce formulaire à tout moment en utilisant le sélecteur déroulant ci-dessus",
    languageBannerBelow:
      "Vous pouvez changer la langue de ce formulaire à tout moment en utilisant le sélecteur déroulant ci-dessous",
    connectionSaveFailed: "Échec de l'enregistrement du formulaire.",
    connectionAutoSaveMessage:
      "Vos modifications seront enregistrées automatiquement lors de la complétion du formulaire.",
    connectionTryAgain: "Réessayer",
    delete: "Supprimer",
    other: "Autre",
    otherValue: "Autre valeur",
    paymentProcessedOnSubmit:
      "Le paiement sera traité lors de la soumission de ce formulaire.",
    errorSummaryHeading: "Veuillez corriger les {count} erreurs suivantes :",
    goToFirstError: "Aller à la première erreur",
    requiredQuestion: "Cette question est obligatoire",
    pleaseEnterValidResponse: "Veuillez entrer une réponse valide",
    pleaseEnterValidDate: "Veuillez entrer une date valide",
    pleaseEnterValidEmail: "Veuillez entrer une adresse e-mail valide",
    filesFailedToUpload:
      "Un ou plusieurs fichiers n'ont pas pu être téléchargés.",
    filesStillUploading:
      "Certains fichiers sont encore en cours de téléchargement.",
    pleaseEnterValidPhone: "Veuillez entrer un numéro de téléphone valide",
    appointmentMustBeCompleted: "Le champ de rendez-vous doit être complété",
    paymentMustBeCompleted: "Le paiement doit être effectué",
    pleaseSelectOptionsThatApply:
      "Veuillez sélectionner toutes les options qui s'appliquent",
    pleaseMakeASelection: "Veuillez faire une sélection",
    saveForm: "Enregistrer le formulaire",
    formHasBeenCompleted: "a été complété",
    formHasBeenClosed: "a été fermé",
    formNotCurrentlyAvailable: "n'est pas disponible actuellement",
    formHasBeenRemoved: "a été supprimé",
    formStatusMessage:
      "Ce formulaire {status}. Veuillez contacter {contact} pour toute autre question.",
    contactFallbackUs: "nous",
    signature: "Signature",
    signatureRequired: "Signature requise",
    signatureDraw: "Dessiner",
    signatureType: "Saisir",
    signatureClear: "Effacer",
    signatureEditingDisabled: "La modification de la signature est désactivée",
    digitalSignatureCalloutBody:
      "Lorsque vous sélectionnez {action}, vous serez redirigé vers une page {provider} sécurisée pour apposer votre signature.",
    continueLabel: "Continuer",
    signatureModalRedirect:
      "Ce formulaire nécessite une signature, sélectionnez {action} pour être redirigé vers la page de signature sécurisée",
    signatureModalContact:
      "Ce formulaire nécessite une signature. Veuillez contacter {contact} pour toute autre question.",
  },
  de: {
    add: "Hinzufügen",
    and: "und",
    file: "Datei",
    files: "Dateien",
    next: "Weiter",
    submit: "Absenden",
    submitAndSign: "Absenden und unterschreiben",
    showSections: "Abschnitte anzeigen",
    sections: "Abschnitte",
    summary: "Zusammenfassung",
    reviewResponses: "Bitte überprüfen Sie Ihre Angaben",
    editSection: "Abschnitt bearbeiten",
    indicatesRequiredInformation: "Kennzeichnet erforderliche Informationen",
    confirm: "Bestätigen",
    cancel: "Abbrechen",
    autofillConfirmMessage1:
      "Bevor Sie auf 'Bestätigen' klicken, stellen Sie sicher, dass alle relevanten Dokumente hochgeladen wurden.",
    autofillConfirmMessage2:
      "Nach der Bestätigung wird das Formular automatisch ausgefüllt. Weitere Uploads oder die Nutzung des Autofill-Assistenten sind dann nicht mehr möglich.",
    autofillFailedMessage:
      "Wir konnten die von Ihnen hochgeladenen Dokumente nicht lesen. Bitte füllen Sie das Formular unten aus.",
    appointmentSelectAttendee: "Teilnehmer auswählen",
    appointmentAvailableAttendees: "Verfügbare Teilnehmer",
    appointmentNoAttendeesAvailable: "Keine Teilnehmer verfügbar",
    appointmentFirstAvailable: "Erstver­fügbar",
    appointmentInPersonMeeting: "Persönliches Treffen",
    appointmentPhoneMeeting: "Telefonisches Treffen",
    appointmentTimezone: "Zeitzone",
    appointmentNoOptions: "Keine Optionen",
    appointmentTime: "Uhrzeit",
    appointmentClearSelection: "Auswahl löschen",
    appointmentNoAppointmentMade: "Kein Termin vereinbart",
    appointmentSelectedAttendee: "Ausgewählter Teilnehmer",
    appointmentDate: "Datum",
    appointmentMonth: "Monat",
    appointmentYear: "Jahr",
    appointmentPreviousMonth: "Vorheriger Monat",
    appointmentNextMonth: "Nächster Monat",
    appointmentSelectedMessage:
      "Sie haben {date} um {time} für Ihr Treffen ausgewählt",
    appointmentNotSelectedMessage:
      "Es wird kein Treffen geplant, da Sie keine Uhrzeit ausgewählt haben",
    minute: "Minute",
    minutes: "Minuten",
    hour: "Stunde",
    hours: "Stunden",
    formCompletedOn: "Formular abgeschlossen am",
    languageBannerAbove:
      "Sie können die Sprache dieses Formulars jederzeit über die Dropdown-Auswahl oben ändern",
    languageBannerBelow:
      "Sie können die Sprache dieses Formulars jederzeit über die Dropdown-Auswahl unten ändern",
    connectionSaveFailed: "Das Formular konnte nicht gespeichert werden.",
    connectionAutoSaveMessage:
      "Ihre Änderungen werden beim Ausfüllen des Formulars automatisch gespeichert.",
    connectionTryAgain: "Erneut versuchen",
    delete: "Löschen",
    other: "Sonstiges",
    otherValue: "Anderer Wert",
    paymentProcessedOnSubmit:
      "Die Zahlung wird bei Absenden dieses Formulars verarbeitet.",
    errorSummaryHeading: "Bitte korrigieren Sie die folgenden {count} Fehler:",
    goToFirstError: "Zum ersten Fehler gehen",
    requiredQuestion: "Dies ist eine Pflichtfrage",
    pleaseEnterValidResponse: "Bitte geben Sie eine gültige Antwort ein",
    pleaseEnterValidDate: "Bitte geben Sie ein gültiges Datum ein",
    pleaseEnterValidEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
    filesFailedToUpload:
      "Eine oder mehrere Dateien konnten nicht hochgeladen werden.",
    filesStillUploading: "Einige Dateien werden noch hochgeladen.",
    pleaseEnterValidPhone: "Bitte geben Sie eine gültige Telefonnummer ein",
    appointmentMustBeCompleted: "Das Terminfeld muss ausgefüllt werden",
    paymentMustBeCompleted: "Die Zahlung muss abgeschlossen werden",
    pleaseSelectOptionsThatApply: "Bitte wählen Sie alle zutreffenden Optionen",
    pleaseMakeASelection: "Bitte treffen Sie eine Auswahl",
    saveForm: "Formular speichern",
    formHasBeenCompleted: "wurde abgeschlossen",
    formHasBeenClosed: "wurde geschlossen",
    formNotCurrentlyAvailable: "ist derzeit nicht verfügbar",
    formHasBeenRemoved: "wurde entfernt",
    formStatusMessage:
      "Dieses Formular {status}. Bitte wenden Sie sich an {contact}, wenn Sie weitere Fragen haben.",
    contactFallbackUs: "uns",
    signature: "Unterschrift",
    signatureRequired: "Unterschrift erforderlich",
    signatureDraw: "Zeichnen",
    signatureType: "Tippen",
    signatureClear: "Löschen",
    signatureEditingDisabled: "Bearbeitung der Unterschrift ist deaktiviert",
    digitalSignatureCalloutBody:
      "Wenn Sie {action} auswählen, werden Sie zu einer sicheren {provider}-Seite weitergeleitet, um Ihre Unterschrift zu leisten.",
    continueLabel: "Weiter",
    signatureModalRedirect:
      "Dieses Formular erfordert eine Unterschrift. Wählen Sie {action}, um zur sicheren Signaturseite weitergeleitet zu werden",
    signatureModalContact:
      "Dieses Formular erfordert eine Unterschrift. Bitte wenden Sie sich an {contact}, wenn Sie weitere Fragen haben.",
  },
  it: {
    add: "Aggiungi",
    and: "e",
    file: "file",
    files: "file",
    next: "Avanti",
    submit: "Invia",
    submitAndSign: "Invia e firma",
    showSections: "Mostra sezioni",
    sections: "Sezioni",
    summary: "Riepilogo",
    reviewResponses: "Si prega di rivedere le risposte",
    editSection: "Modifica sezione",
    indicatesRequiredInformation: "Indica le informazioni richieste",
    confirm: "Conferma",
    cancel: "Annulla",
    autofillConfirmMessage1:
      "Prima di cliccare su 'Conferma', assicurati che tutti i documenti pertinenti siano stati caricati.",
    autofillConfirmMessage2:
      "Una volta confermato, compileremo automaticamente il modulo e non sarà più possibile caricare altri file né utilizzare l'Assistente di compilazione automatica.",
    autofillFailedMessage:
      "Non siamo riusciti a leggere i documenti che hai caricato. Compila il modulo qui sotto.",
    appointmentSelectAttendee: "Seleziona partecipante",
    appointmentAvailableAttendees: "Partecipanti disponibili",
    appointmentNoAttendeesAvailable: "Nessun partecipante disponibile",
    appointmentFirstAvailable: "Primo disponibile",
    appointmentInPersonMeeting: "Riunione di persona",
    appointmentPhoneMeeting: "Riunione telefonica",
    appointmentTimezone: "Fuso orario",
    appointmentNoOptions: "Nessuna opzione",
    appointmentTime: "Ora",
    appointmentClearSelection: "Cancella selezione",
    appointmentNoAppointmentMade: "Nessun appuntamento fissato",
    appointmentSelectedAttendee: "Partecipante selezionato",
    appointmentDate: "Data",
    appointmentMonth: "Mese",
    appointmentYear: "Anno",
    appointmentPreviousMonth: "Mese precedente",
    appointmentNextMonth: "Mese successivo",
    appointmentSelectedMessage:
      "Hai selezionato {date} alle {time} per la tua riunione",
    appointmentNotSelectedMessage:
      "Non verrà pianificata alcuna riunione perché non hai selezionato un orario",
    minute: "minuto",
    minutes: "minuti",
    hour: "ora",
    hours: "ore",
    formCompletedOn: "Modulo completato il",
    languageBannerAbove:
      "Puoi cambiare la lingua di questo modulo in qualsiasi momento usando il menu a discesa sopra",
    languageBannerBelow:
      "Puoi cambiare la lingua di questo modulo in qualsiasi momento usando il menu a discesa sotto",
    connectionSaveFailed: "Impossibile salvare il modulo.",
    connectionAutoSaveMessage:
      "Le modifiche verranno salvate automaticamente durante la compilazione del modulo.",
    connectionTryAgain: "Riprova",
    delete: "Elimina",
    other: "Altro",
    otherValue: "Altro valore",
    paymentProcessedOnSubmit:
      "Il pagamento verrà elaborato all'invio di questo modulo.",
    errorSummaryHeading: "Correggi le seguenti {count} errori:",
    goToFirstError: "Vai al primo errore",
    requiredQuestion: "Questa domanda è obbligatoria",
    pleaseEnterValidResponse: "Inserisci una risposta valida",
    pleaseEnterValidDate: "Inserisci una data valida",
    pleaseEnterValidEmail: "Inserisci un indirizzo email valido",
    filesFailedToUpload: "Uno o più file non sono stati caricati.",
    filesStillUploading: "Alcuni file sono ancora in fase di caricamento.",
    pleaseEnterValidPhone: "Inserisci un numero di telefono valido",
    appointmentMustBeCompleted: "Il campo appuntamento deve essere completato",
    paymentMustBeCompleted: "Il pagamento deve essere completato",
    pleaseSelectOptionsThatApply: "Seleziona tutte le opzioni applicabili",
    pleaseMakeASelection: "Effettua una selezione",
    saveForm: "Salva modulo",
    formHasBeenCompleted: "è stato completato",
    formHasBeenClosed: "è stato chiuso",
    formNotCurrentlyAvailable: "non è attualmente disponibile",
    formHasBeenRemoved: "è stato rimosso",
    formStatusMessage:
      "Questo modulo {status}. Contatta {contact} per ulteriori domande.",
    contactFallbackUs: "noi",
    signature: "Firma",
    signatureRequired: "Firma richiesta",
    signatureDraw: "Disegna",
    signatureType: "Digita",
    signatureClear: "Cancella",
    signatureEditingDisabled: "La modifica della firma è disabilitata",
    digitalSignatureCalloutBody:
      "Quando selezioni {action} verrai indirizzato a una pagina {provider} sicura per apporre la tua firma.",
    continueLabel: "Continua",
    signatureModalRedirect:
      "Questo modulo richiede una firma, seleziona {action} per essere reindirizzato alla pagina di firma sicura",
    signatureModalContact:
      "Questo modulo richiede una firma. Contatta {contact} per ulteriori domande.",
  },
  ja: {
    add: "追加",
    and: "と",
    file: "ファイル",
    files: "ファイル",
    next: "次へ",
    submit: "送信",
    submitAndSign: "送信して署名",
    showSections: "セクションを表示",
    sections: "セクション",
    summary: "要約",
    reviewResponses: "回答を確認してください",
    editSection: "セクションを編集",
    indicatesRequiredInformation: "必須情報を示します",
    confirm: "確認",
    cancel: "キャンセル",
    autofillConfirmMessage1:
      "「確認」をクリックする前に、関連するすべての書類がアップロードされていることを確認してください。",
    autofillConfirmMessage2:
      "確認後、フォームは自動入力され、それ以降のアップロードや「自動入力アシスタント」の使用はできなくなります。",
    autofillFailedMessage:
      "アップロードされた書類を読み取れませんでした。下のフォームにご記入ください。",
    appointmentSelectAttendee: "参加者を選択",
    appointmentAvailableAttendees: "参加可能なメンバー",
    appointmentNoAttendeesAvailable: "参加可能なメンバーはいません",
    appointmentFirstAvailable: "最初の空き",
    appointmentInPersonMeeting: "対面ミーティング",
    appointmentPhoneMeeting: "電話ミーティング",
    appointmentTimezone: "タイムゾーン",
    appointmentNoOptions: "オプションなし",
    appointmentTime: "時間",
    appointmentClearSelection: "選択をクリア",
    appointmentNoAppointmentMade: "予約なし",
    appointmentSelectedAttendee: "選択された参加者",
    appointmentDate: "日付",
    appointmentMonth: "月",
    appointmentYear: "年",
    appointmentPreviousMonth: "前月",
    appointmentNextMonth: "翌月",
    appointmentSelectedMessage: "{date} {time} のミーティングを選択しました",
    appointmentNotSelectedMessage:
      "時間が選択されていないため、ミーティングは予約されません",
    minute: "分",
    minutes: "分",
    hour: "時間",
    hours: "時間",
    formCompletedOn: "フォームは",
    languageBannerAbove:
      "上のドロップダウンでいつでもこのフォームの言語を変更できます",
    languageBannerBelow:
      "下のドロップダウンでいつでもこのフォームの言語を変更できます",
    connectionSaveFailed: "フォームの保存に失敗しました。",
    connectionAutoSaveMessage:
      "フォームの記入に合わせて変更は自動的に保存されます。",
    connectionTryAgain: "再試行",
    delete: "削除",
    other: "その他",
    otherValue: "その他の値",
    paymentProcessedOnSubmit:
      "このフォームを送信するとお支払いが処理されます。",
    errorSummaryHeading: "以下の {count} 件のエラーを修正してください：",
    goToFirstError: "最初のエラーへ移動",
    requiredQuestion: "この質問は必須です",
    pleaseEnterValidResponse: "有効な回答を入力してください",
    pleaseEnterValidDate: "有効な日付を入力してください",
    pleaseEnterValidEmail: "有効なメールアドレスを入力してください",
    filesFailedToUpload: "1 つ以上のファイルのアップロードに失敗しました。",
    filesStillUploading: "一部のファイルはまだアップロード中です。",
    pleaseEnterValidPhone: "有効な電話番号を入力してください",
    appointmentMustBeCompleted: "予約フィールドを入力してください",
    paymentMustBeCompleted: "支払いを完了してください",
    pleaseSelectOptionsThatApply: "該当するオプションをすべて選択してください",
    pleaseMakeASelection: "選択してください",
    saveForm: "フォームを保存",
    formHasBeenCompleted: "は完了しました",
    formHasBeenClosed: "は閉じられました",
    formNotCurrentlyAvailable: "は現在利用できません",
    formHasBeenRemoved: "は削除されました",
    formStatusMessage:
      "このフォーム{status}。ご不明な点がございましたら、{contact}までお問い合わせください。",
    contactFallbackUs: "当社",
    signature: "署名",
    signatureRequired: "署名が必要です",
    signatureDraw: "手書き",
    signatureType: "入力",
    signatureClear: "消去",
    signatureEditingDisabled: "署名の編集は無効になっています",
    digitalSignatureCalloutBody:
      "{action}を選択すると、署名を行うための安全な{provider}ページに移動します。",
    continueLabel: "続行",
    signatureModalRedirect:
      "このフォームには署名が必要です。{action}を選択すると、安全な署名ページに移動します",
    signatureModalContact:
      "このフォームには署名が必要です。ご不明な点がございましたら、{contact}までお問い合わせください。",
  },
  ko: {
    add: "추가",
    and: "및",
    file: "파일",
    files: "파일",
    next: "다음",
    submit: "제출",
    submitAndSign: "제출 및 서명",
    showSections: "섹션 보기",
    sections: "섹션",
    summary: "요약",
    reviewResponses: "답변을 검토해 주세요",
    editSection: "섹션 편집",
    indicatesRequiredInformation: "필수 정보를 나타냅니다",
    confirm: "확인",
    cancel: "취소",
    autofillConfirmMessage1:
      "'확인'을 클릭하기 전에 모든 관련 문서가 업로드되었는지 확인하세요.",
    autofillConfirmMessage2:
      "확인 후에는 양식이 자동으로 작성되며, 이후에는 파일을 추가로 업로드하거나 '자동 작성 도우미'를 사용할 수 없습니다.",
    autofillFailedMessage:
      "업로드하신 문서를 읽을 수 없습니다. 아래 양식을 작성해 주세요.",
    appointmentSelectAttendee: "참석자 선택",
    appointmentAvailableAttendees: "참석 가능한 멤버",
    appointmentNoAttendeesAvailable: "참석 가능한 멤버 없음",
    appointmentFirstAvailable: "첫 번째 가능",
    appointmentInPersonMeeting: "대면 미팅",
    appointmentPhoneMeeting: "전화 미팅",
    appointmentTimezone: "시간대",
    appointmentNoOptions: "옵션 없음",
    appointmentTime: "시간",
    appointmentClearSelection: "선택 지우기",
    appointmentNoAppointmentMade: "예약 없음",
    appointmentSelectedAttendee: "선택된 참석자",
    appointmentDate: "날짜",
    appointmentMonth: "월",
    appointmentYear: "년",
    appointmentPreviousMonth: "이전 달",
    appointmentNextMonth: "다음 달",
    appointmentSelectedMessage: "{date} {time}에 미팅을 선택하셨습니다",
    appointmentNotSelectedMessage:
      "시간을 선택하지 않아 미팅이 예약되지 않습니다",
    minute: "분",
    minutes: "분",
    hour: "시간",
    hours: "시간",
    formCompletedOn: "양식 완료일:",
    languageBannerAbove:
      "위의 드롭다운 선택기를 사용하여 언제든지 이 양식의 언어를 변경할 수 있습니다",
    languageBannerBelow:
      "아래 드롭다운 선택기를 사용하여 언제든지 이 양식의 언어를 변경할 수 있습니다",
    connectionSaveFailed: "양식을 저장하지 못했습니다.",
    connectionAutoSaveMessage:
      "양식을 작성하는 동안 변경 사항이 자동으로 저장됩니다.",
    connectionTryAgain: "다시 시도",
    delete: "삭제",
    other: "기타",
    otherValue: "기타 값",
    paymentProcessedOnSubmit: "이 양식을 제출하면 결제가 처리됩니다.",
    errorSummaryHeading: "다음 {count}개 오류를 수정해 주세요:",
    goToFirstError: "첫 번째 오류로 이동",
    requiredQuestion: "필수 질문입니다",
    pleaseEnterValidResponse: "유효한 응답을 입력해 주세요",
    pleaseEnterValidDate: "유효한 날짜를 입력해 주세요",
    pleaseEnterValidEmail: "유효한 이메일 주소를 입력해 주세요",
    filesFailedToUpload: "하나 이상의 파일 업로드에 실패했습니다.",
    filesStillUploading: "일부 파일이 아직 업로드 중입니다.",
    pleaseEnterValidPhone: "유효한 전화번호를 입력해 주세요",
    appointmentMustBeCompleted: "약속 필드를 완료해 주세요",
    paymentMustBeCompleted: "결제를 완료해 주세요",
    pleaseSelectOptionsThatApply: "해당하는 모든 옵션을 선택해 주세요",
    pleaseMakeASelection: "선택해 주세요",
    saveForm: "양식 저장",
    formHasBeenCompleted: "이(가) 완료되었습니다",
    formHasBeenClosed: "이(가) 닫혔습니다",
    formNotCurrentlyAvailable: "은(는) 현재 이용할 수 없습니다",
    formHasBeenRemoved: "이(가) 삭제되었습니다",
    formStatusMessage:
      "이 양식{status}. 추가 문의 사항이 있으시면 {contact}에 문의해 주세요.",
    contactFallbackUs: "저희",
    signature: "서명",
    signatureRequired: "서명 필요",
    signatureDraw: "그리기",
    signatureType: "입력",
    signatureClear: "지우기",
    signatureEditingDisabled: "서명 편집이 비활성화되었습니다",
    digitalSignatureCalloutBody:
      "{action}을(를) 선택하면 서명을 위한 보안 {provider} 페이지로 이동합니다.",
    continueLabel: "계속",
    signatureModalRedirect:
      "이 양식에는 서명이 필요합니다. {action}을(를) 선택하면 보안 서명 페이지로 이동합니다",
    signatureModalContact:
      "이 양식에는 서명이 필요합니다. 추가 문의 사항이 있으시면 {contact}에 문의해 주세요.",
  },
  pt: {
    add: "Adicionar",
    and: "e",
    file: "arquivo",
    files: "arquivos",
    next: "Próximo",
    submit: "Enviar",
    submitAndSign: "Enviar e assinar",
    showSections: "Mostrar seções",
    sections: "Seções",
    summary: "Resumo",
    reviewResponses: "Por favor, revise suas respostas",
    editSection: "Editar seção",
    indicatesRequiredInformation: "Indica informações obrigatórias",
    confirm: "Confirmar",
    cancel: "Cancelar",
    autofillConfirmMessage1:
      "Antes de clicar em 'Confirmar', certifique-se de que todos os documentos relevantes foram carregados.",
    autofillConfirmMessage2:
      "Após a confirmação, preencheremos o formulário automaticamente e não será mais possível enviar novos arquivos ou usar o 'Assistente de Preenchimento Automático'.",
    autofillFailedMessage:
      "Não conseguimos ler os documentos que você enviou. Preencha o formulário abaixo.",
    appointmentSelectAttendee: "Selecionar participante",
    appointmentAvailableAttendees: "Participantes disponíveis",
    appointmentNoAttendeesAvailable: "Nenhum participante disponível",
    appointmentFirstAvailable: "Primeiro disponível",
    appointmentInPersonMeeting: "Reunião presencial",
    appointmentPhoneMeeting: "Reunião por telefone",
    appointmentTimezone: "Fuso horário",
    appointmentNoOptions: "Sem opções",
    appointmentTime: "Hora",
    appointmentClearSelection: "Limpar seleção",
    appointmentNoAppointmentMade: "Nenhum agendamento realizado",
    appointmentSelectedAttendee: "Participante selecionado",
    appointmentDate: "Data",
    appointmentMonth: "Mês",
    appointmentYear: "Ano",
    appointmentPreviousMonth: "Mês anterior",
    appointmentNextMonth: "Próximo mês",
    appointmentSelectedMessage:
      "Você selecionou {date} às {time} para sua reunião",
    appointmentNotSelectedMessage:
      "Uma reunião não será agendada pois você não selecionou um horário",
    minute: "minuto",
    minutes: "minutos",
    hour: "hora",
    hours: "horas",
    formCompletedOn: "Formulário concluído em",
    languageBannerAbove:
      "Você pode alterar o idioma deste formulário a qualquer momento usando o seletor suspenso acima",
    languageBannerBelow:
      "Você pode alterar o idioma deste formulário a qualquer momento usando o seletor suspenso abaixo",
    connectionSaveFailed: "Falha ao salvar seu formulário.",
    connectionAutoSaveMessage:
      "Suas alterações serão salvas automaticamente conforme você preenche o formulário.",
    connectionTryAgain: "Tentar novamente",
    delete: "Excluir",
    other: "Outro",
    otherValue: "Outro valor",
    paymentProcessedOnSubmit:
      "O pagamento será processado após o envio deste formulário.",
    errorSummaryHeading: "Corrija os seguintes {count} erros:",
    goToFirstError: "Ir ao primeiro erro",
    requiredQuestion: "Esta é uma pergunta obrigatória",
    pleaseEnterValidResponse: "Por favor, insira uma resposta válida",
    pleaseEnterValidDate: "Por favor, insira uma data válida",
    pleaseEnterValidEmail: "Por favor, insira um endereço de e-mail válido",
    filesFailedToUpload: "Um ou mais arquivos falharam ao carregar.",
    filesStillUploading: "Alguns arquivos ainda estão sendo carregados.",
    pleaseEnterValidPhone: "Por favor, insira um número de telefone válido",
    appointmentMustBeCompleted: "O campo de agendamento deve ser preenchido",
    paymentMustBeCompleted: "O pagamento deve ser concluído",
    pleaseSelectOptionsThatApply:
      "Por favor, selecione todas as opções aplicáveis",
    pleaseMakeASelection: "Por favor, faça uma seleção",
    saveForm: "Salvar formulário",
    formHasBeenCompleted: "foi concluído",
    formHasBeenClosed: "foi encerrado",
    formNotCurrentlyAvailable: "não está disponível no momento",
    formHasBeenRemoved: "foi removido",
    formStatusMessage:
      "Este formulário {status}. Entre em contato com {contact} em caso de dúvidas.",
    contactFallbackUs: "nós",
    signature: "Assinatura",
    signatureRequired: "Assinatura necessária",
    signatureDraw: "Desenhar",
    signatureType: "Digitar",
    signatureClear: "Limpar",
    signatureEditingDisabled: "A edição da assinatura está desativada",
    digitalSignatureCalloutBody:
      "Ao selecionar {action}, você será direcionado para uma página {provider} segura para aplicar sua assinatura.",
    continueLabel: "Continuar",
    signatureModalRedirect:
      "Este formulário requer uma assinatura, selecione {action} para ser redirecionado para a página de assinatura segura",
    signatureModalContact:
      "Este formulário requer uma assinatura. Entre em contato com {contact} em caso de dúvidas.",
  },
  es: {
    add: "Añadir",
    and: "y",
    file: "archivo",
    files: "archivos",
    next: "Siguiente",
    submit: "Enviar",
    submitAndSign: "Enviar y firmar",
    showSections: "Mostrar secciones",
    sections: "Secciones",
    summary: "Resumen",
    reviewResponses: "Por favor revise sus respuestas",
    editSection: "Editar sección",
    indicatesRequiredInformation: "Indica información obligatoria",
    confirm: "Confirmar",
    cancel: "Cancelar",
    autofillConfirmMessage1:
      "Antes de hacer clic en 'Confirmar', asegúrese de que todos los documentos relevantes estén cargados.",
    autofillConfirmMessage2:
      "Una vez confirmado, completaremos el formulario automáticamente y no será posible cargar más archivos ni usar el 'Asistente de autocompletado'.",
    autofillFailedMessage:
      "No pudimos leer los documentos que cargó. Complete el formulario a continuación.",
    appointmentSelectAttendee: "Seleccionar asistente",
    appointmentAvailableAttendees: "Asistentes disponibles",
    appointmentNoAttendeesAvailable: "No hay asistentes disponibles",
    appointmentFirstAvailable: "Primer disponible",
    appointmentInPersonMeeting: "Reunión presencial",
    appointmentPhoneMeeting: "Reunión telefónica",
    appointmentTimezone: "Zona horaria",
    appointmentNoOptions: "Sin opciones",
    appointmentTime: "Hora",
    appointmentClearSelection: "Borrar selección",
    appointmentNoAppointmentMade: "No se ha realizado ninguna cita",
    appointmentSelectedAttendee: "Asistente seleccionado",
    appointmentDate: "Fecha",
    appointmentMonth: "Mes",
    appointmentYear: "Año",
    appointmentPreviousMonth: "Mes anterior",
    appointmentNextMonth: "Mes siguiente",
    appointmentSelectedMessage:
      "Ha seleccionado {date} a las {time} para su reunión",
    appointmentNotSelectedMessage:
      "No se programará una reunión ya que no ha seleccionado una hora",
    minute: "minuto",
    minutes: "minutos",
    hour: "hora",
    hours: "horas",
    formCompletedOn: "Formulario completado el",
    languageBannerAbove:
      "Puede cambiar el idioma de este formulario en cualquier momento usando el selector desplegable de arriba",
    languageBannerBelow:
      "Puede cambiar el idioma de este formulario en cualquier momento usando el selector desplegable de abajo",
    connectionSaveFailed: "Error al guardar el formulario.",
    connectionAutoSaveMessage:
      "Sus cambios se guardarán automáticamente mientras completa el formulario.",
    connectionTryAgain: "Intentar de nuevo",
    delete: "Borrar",
    other: "Otro",
    otherValue: "Otro valor",
    paymentProcessedOnSubmit: "El pago se procesará al enviar este formulario.",
    errorSummaryHeading: "Corrija los siguientes {count} errores:",
    goToFirstError: "Ir al primer error",
    requiredQuestion: "Esta es una pregunta obligatoria",
    pleaseEnterValidResponse: "Por favor, introduzca una respuesta válida",
    pleaseEnterValidDate: "Por favor, introduzca una fecha válida",
    pleaseEnterValidEmail:
      "Por favor, introduzca una dirección de correo válida",
    filesFailedToUpload: "Uno o más archivos no se pudieron cargar.",
    filesStillUploading: "Algunos archivos aún se están cargando.",
    pleaseEnterValidPhone: "Por favor, introduzca un número de teléfono válido",
    appointmentMustBeCompleted: "El campo de cita debe completarse",
    paymentMustBeCompleted: "El pago debe completarse",
    pleaseSelectOptionsThatApply:
      "Por favor, seleccione todas las opciones que apliquen",
    pleaseMakeASelection: "Por favor, haga una selección",
    saveForm: "Guardar formulario",
    formHasBeenCompleted: "ha sido completado",
    formHasBeenClosed: "ha sido cerrado",
    formNotCurrentlyAvailable: "no está disponible actualmente",
    formHasBeenRemoved: "ha sido eliminado",
    formStatusMessage:
      "Este formulario {status}. Póngase en contacto con {contact} si tiene más preguntas.",
    contactFallbackUs: "nosotros",
    signature: "Firma",
    signatureRequired: "Se requiere firma",
    signatureDraw: "Dibujar",
    signatureType: "Escribir",
    signatureClear: "Borrar",
    signatureEditingDisabled: "La edición de la firma está deshabilitada",
    digitalSignatureCalloutBody:
      "Al seleccionar {action}, se le dirigirá a una página segura de {provider} para aplicar su firma.",
    continueLabel: "Continuar",
    signatureModalRedirect:
      "Este formulario requiere una firma, seleccione {action} para ser redirigido a la página de firma segura",
    signatureModalContact:
      "Este formulario requiere una firma. Póngase en contacto con {contact} si tiene más preguntas.",
  },
  vi: {
    add: "Thêm",
    and: "và",
    file: "tệp",
    files: "tệp",
    next: "Tiếp theo",
    submit: "Gửi",
    submitAndSign: "Gửi và ký",
    showSections: "Hiển thị các phần",
    sections: "Các phần",
    summary: "Tóm tắt",
    reviewResponses: "Vui lòng xem lại câu trả lời của bạn",
    editSection: "Chỉnh sửa phần",
    indicatesRequiredInformation: "Chỉ ra thông tin bắt buộc",
    confirm: "Xác nhận",
    cancel: "Hủy",
    autofillConfirmMessage1:
      "Trước khi nhấp vào 'Xác nhận', hãy đảm bảo rằng tất cả các tài liệu liên quan đã được tải lên.",
    autofillConfirmMessage2:
      "Sau khi xác nhận, chúng tôi sẽ tự động điền biểu mẫu và bạn sẽ không thể tải thêm tệp hoặc sử dụng 'Trợ lý tự động điền' nữa.",
    autofillFailedMessage:
      "Chúng tôi không thể đọc các tài liệu bạn đã tải lên. Vui lòng điền vào biểu mẫu bên dưới.",
    appointmentSelectAttendee: "Chọn người tham dự",
    appointmentAvailableAttendees: "Người tham dự có sẵn",
    appointmentNoAttendeesAvailable: "Không có người tham dự",
    appointmentFirstAvailable: "Sớm nhất có thể",
    appointmentInPersonMeeting: "Họp trực tiếp",
    appointmentPhoneMeeting: "Họp qua điện thoại",
    appointmentTimezone: "Múi giờ",
    appointmentNoOptions: "Không có lựa chọn",
    appointmentTime: "Thời gian",
    appointmentClearSelection: "Xóa lựa chọn",
    appointmentNoAppointmentMade: "Chưa đặt lịch hẹn",
    appointmentSelectedAttendee: "Người tham dự đã chọn",
    appointmentDate: "Ngày",
    appointmentMonth: "Tháng",
    appointmentYear: "Năm",
    appointmentPreviousMonth: "Tháng trước",
    appointmentNextMonth: "Tháng sau",
    appointmentSelectedMessage: "Bạn đã chọn {date} lúc {time} cho cuộc họp",
    appointmentNotSelectedMessage:
      "Cuộc họp sẽ không được lên lịch vì bạn chưa chọn thời gian",
    minute: "phút",
    minutes: "phút",
    hour: "giờ",
    hours: "giờ",
    formCompletedOn: "Đã hoàn thành biểu mẫu vào",
    languageBannerAbove:
      "Bạn có thể thay đổi ngôn ngữ của biểu mẫu này bất cứ lúc nào bằng bộ chọn thả xuống ở trên",
    languageBannerBelow:
      "Bạn có thể thay đổi ngôn ngữ của biểu mẫu này bất cứ lúc nào bằng bộ chọn thả xuống ở dưới",
    connectionSaveFailed: "Không thể lưu biểu mẫu.",
    connectionAutoSaveMessage:
      "Thay đổi của bạn sẽ được lưu tự động khi bạn hoàn thành biểu mẫu.",
    connectionTryAgain: "Thử lại",
    delete: "Xóa",
    other: "Khác",
    otherValue: "Giá trị khác",
    paymentProcessedOnSubmit: "Thanh toán sẽ được xử lý khi gửi biểu mẫu này.",
    errorSummaryHeading: "Vui lòng sửa {count} lỗi sau:",
    goToFirstError: "Đi đến lỗi đầu tiên",
    requiredQuestion: "Đây là câu hỏi bắt buộc",
    pleaseEnterValidResponse: "Vui lòng nhập phản hồi hợp lệ",
    pleaseEnterValidDate: "Vui lòng nhập ngày hợp lệ",
    pleaseEnterValidEmail: "Vui lòng nhập địa chỉ email hợp lệ",
    filesFailedToUpload: "Một hoặc nhiều tệp tải lên thất bại.",
    filesStillUploading: "Một số tệp vẫn đang được tải lên.",
    pleaseEnterValidPhone: "Vui lòng nhập số điện thoại hợp lệ",
    appointmentMustBeCompleted: "Trường lịch hẹn phải được hoàn thành",
    paymentMustBeCompleted: "Thanh toán phải được hoàn thành",
    pleaseSelectOptionsThatApply: "Vui lòng chọn tất cả các tùy chọn áp dụng",
    pleaseMakeASelection: "Vui lòng thực hiện lựa chọn",
    saveForm: "Lưu biểu mẫu",
    formHasBeenCompleted: "đã hoàn thành",
    formHasBeenClosed: "đã bị đóng",
    formNotCurrentlyAvailable: "hiện không khả dụng",
    formHasBeenRemoved: "đã bị xóa",
    formStatusMessage:
      "Biểu mẫu này {status}. Vui lòng liên hệ {contact} nếu bạn có thêm câu hỏi.",
    contactFallbackUs: "chúng tôi",
    signature: "Chữ ký",
    signatureRequired: "Yêu cầu chữ ký",
    signatureDraw: "Vẽ",
    signatureType: "Nhập",
    signatureClear: "Xóa",
    signatureEditingDisabled: "Chỉnh sửa chữ ký đã bị vô hiệu hóa",
    digitalSignatureCalloutBody:
      "Khi bạn chọn {action}, bạn sẽ được chuyển đến trang {provider} bảo mật để ký.",
    continueLabel: "Tiếp tục",
    signatureModalRedirect:
      "Biểu mẫu này yêu cầu chữ ký, hãy chọn {action} để được chuyển đến trang ký bảo mật",
    signatureModalContact:
      "Biểu mẫu này yêu cầu chữ ký. Vui lòng liên hệ {contact} nếu bạn có thêm câu hỏi.",
  },
  pl: {
    add: "Dodaj",
    and: "i",
    file: "plik",
    files: "pliki",
    next: "Dalej",
    submit: "Wyślij",
    submitAndSign: "Wyślij i podpisz",
    showSections: "Pokaż sekcje",
    sections: "Sekcje",
    summary: "Podsumowanie",
    reviewResponses: "Sprawdź swoje odpowiedzi",
    editSection: "Edytuj sekcję",
    indicatesRequiredInformation: "Oznacza wymagane informacje",
    confirm: "Potwierdź",
    cancel: "Anuluj",
    autofillConfirmMessage1:
      "Przed kliknięciem „Potwierdź” upewnij się, że wszystkie odpowiednie dokumenty zostały przesłane.",
    autofillConfirmMessage2:
      "Po potwierdzeniu automatycznie wypełnimy formularz i dalsze przesyłanie plików lub korzystanie z Asystenta autouzupełniania nie będzie możliwe.",
    autofillFailedMessage:
      "Nie mogliśmy odczytać przesłanych przez Ciebie dokumentów. Prosimy wypełnić formularz poniżej.",
    appointmentSelectAttendee: "Wybierz uczestnika",
    appointmentAvailableAttendees: "Dostępni uczestnicy",
    appointmentNoAttendeesAvailable: "Brak dostępnych uczestników",
    appointmentFirstAvailable: "Najbliższy dostępny termin",
    appointmentInPersonMeeting: "Spotkanie osobiste",
    appointmentPhoneMeeting: "Spotkanie telefoniczne",
    appointmentTimezone: "Strefa czasowa",
    appointmentNoOptions: "Brak opcji",
    appointmentTime: "Godzina",
    appointmentClearSelection: "Wyczyść wybór",
    appointmentNoAppointmentMade: "Nie umówiono spotkania",
    appointmentSelectedAttendee: "Wybrany uczestnik",
    appointmentDate: "Data",
    appointmentMonth: "Miesiąc",
    appointmentYear: "Rok",
    appointmentPreviousMonth: "Poprzedni miesiąc",
    appointmentNextMonth: "Następny miesiąc",
    appointmentSelectedMessage: "Wybrałeś {date} o {time} na swoje spotkanie",
    appointmentNotSelectedMessage:
      "Spotkanie nie zostanie zaplanowane, ponieważ nie wybrano godziny",
    minute: "minuta",
    minutes: "minuty",
    hour: "godzina",
    hours: "godziny",
    formCompletedOn: "Formularz wypełniono",
    languageBannerAbove:
      "Możesz zmienić język tego formularza w dowolnym momencie, korzystając z listy rozwijanej powyżej",
    languageBannerBelow:
      "Możesz zmienić język tego formularza w dowolnym momencie, korzystając z listy rozwijanej poniżej",
    connectionSaveFailed: "Nie udało się zapisać formularza.",
    connectionAutoSaveMessage:
      "Twoje zmiany będą zapisywane automatycznie podczas wypełniania formularza.",
    connectionTryAgain: "Spróbuj ponownie",
    delete: "Usuń",
    other: "Inne",
    otherValue: "Inna wartość",
    paymentProcessedOnSubmit:
      "Płatność zostanie przetworzona po wysłaniu tego formularza.",
    errorSummaryHeading: "Popraw następujące {count} błędy:",
    goToFirstError: "Przejdź do pierwszego błędu",
    requiredQuestion: "To pytanie jest wymagane",
    pleaseEnterValidResponse: "Wprowadź prawidłową odpowiedź",
    pleaseEnterValidDate: "Wprowadź prawidłową datę",
    pleaseEnterValidEmail: "Wprowadź prawidłowy adres e-mail",
    filesFailedToUpload: "Nie udało się przesłać co najmniej jednego pliku.",
    filesStillUploading: "Niektóre pliki są nadal przesyłane.",
    pleaseEnterValidPhone: "Wprowadź prawidłowy numer telefonu",
    appointmentMustBeCompleted: "Pole spotkania musi zostać wypełnione",
    paymentMustBeCompleted: "Płatność musi zostać zrealizowana",
    pleaseSelectOptionsThatApply: "Wybierz wszystkie pasujące opcje",
    pleaseMakeASelection: "Dokonaj wyboru",
    saveForm: "Zapisz formularz",
    formHasBeenCompleted: "został wypełniony",
    formHasBeenClosed: "został zamknięty",
    formNotCurrentlyAvailable: "nie jest obecnie dostępny",
    formHasBeenRemoved: "został usunięty",
    formStatusMessage:
      "Ten formularz {status}. Skontaktuj się z {contact} w razie dalszych pytań.",
    contactFallbackUs: "nami",
    signature: "Podpis",
    signatureRequired: "Wymagany podpis",
    signatureDraw: "Narysuj",
    signatureType: "Wpisz",
    signatureClear: "Wyczyść",
    signatureEditingDisabled: "Edycja podpisu jest wyłączona",
    digitalSignatureCalloutBody:
      "Po wybraniu {action} zostaniesz przeniesiony na bezpieczną stronę {provider}, aby złożyć podpis.",
    continueLabel: "Kontynuuj",
    signatureModalRedirect:
      "Ten formularz wymaga podpisu, wybierz {action}, aby przejść do bezpiecznej strony podpisu",
    signatureModalContact:
      "Ten formularz wymaga podpisu. Skontaktuj się z {contact} w razie dalszych pytań.",
  },
  ru: {
    add: "Добавить",
    and: "и",
    file: "файл",
    files: "файлы",
    next: "Далее",
    submit: "Отправить",
    submitAndSign: "Отправить и подписать",
    showSections: "Показать разделы",
    sections: "Разделы",
    summary: "Сводка",
    reviewResponses: "Пожалуйста, проверьте ваши ответы",
    editSection: "Редактировать раздел",
    indicatesRequiredInformation: "Указывает на обязательную информацию",
    confirm: "Подтвердить",
    cancel: "Отмена",
    autofillConfirmMessage1:
      "Перед нажатием «Подтвердить» убедитесь, что все необходимые документы загружены.",
    autofillConfirmMessage2:
      "После подтверждения мы автоматически заполним форму, и дальнейшая загрузка файлов или использование «Помощника автозаполнения» будет невозможны.",
    autofillFailedMessage:
      "Нам не удалось прочитать загруженные вами документы. Пожалуйста, заполните форму ниже.",
    appointmentSelectAttendee: "Выберите участника",
    appointmentAvailableAttendees: "Доступные участники",
    appointmentNoAttendeesAvailable: "Нет доступных участников",
    appointmentFirstAvailable: "Ближайший доступный",
    appointmentInPersonMeeting: "Личная встреча",
    appointmentPhoneMeeting: "Телефонная встреча",
    appointmentTimezone: "Часовой пояс",
    appointmentNoOptions: "Нет вариантов",
    appointmentTime: "Время",
    appointmentClearSelection: "Очистить выбор",
    appointmentNoAppointmentMade: "Встреча не назначена",
    appointmentSelectedAttendee: "Выбранный участник",
    appointmentDate: "Дата",
    appointmentMonth: "Месяц",
    appointmentYear: "Год",
    appointmentPreviousMonth: "Предыдущий месяц",
    appointmentNextMonth: "Следующий месяц",
    appointmentSelectedMessage: "Вы выбрали {date} в {time} для вашей встречи",
    appointmentNotSelectedMessage:
      "Встреча не будет запланирована, так как вы не выбрали время",
    minute: "минута",
    minutes: "минуты",
    hour: "час",
    hours: "часы",
    formCompletedOn: "Форма заполнена",
    languageBannerAbove:
      "Вы можете изменить язык этой формы в любое время с помощью раскрывающегося списка выше",
    languageBannerBelow:
      "Вы можете изменить язык этой формы в любое время с помощью раскрывающегося списка ниже",
    connectionSaveFailed: "Не удалось сохранить форму.",
    connectionAutoSaveMessage:
      "Ваши изменения будут автоматически сохраняться по мере заполнения формы.",
    connectionTryAgain: "Повторить",
    delete: "Удалить",
    other: "Другое",
    otherValue: "Другое значение",
    paymentProcessedOnSubmit:
      "Платёж будет обработан после отправки этой формы.",
    errorSummaryHeading: "Исправьте следующие {count} ошибок:",
    goToFirstError: "Перейти к первой ошибке",
    requiredQuestion: "Это обязательный вопрос",
    pleaseEnterValidResponse: "Введите корректный ответ",
    pleaseEnterValidDate: "Введите корректную дату",
    pleaseEnterValidEmail: "Введите корректный адрес электронной почты",
    filesFailedToUpload: "Не удалось загрузить один или несколько файлов.",
    filesStillUploading: "Некоторые файлы всё ещё загружаются.",
    pleaseEnterValidPhone: "Введите корректный номер телефона",
    appointmentMustBeCompleted: "Поле встречи должно быть заполнено",
    paymentMustBeCompleted: "Платёж должен быть завершён",
    pleaseSelectOptionsThatApply: "Выберите все подходящие варианты",
    pleaseMakeASelection: "Сделайте выбор",
    saveForm: "Сохранить форму",
    formHasBeenCompleted: "заполнена",
    formHasBeenClosed: "закрыта",
    formNotCurrentlyAvailable: "в настоящее время недоступна",
    formHasBeenRemoved: "удалена",
    formStatusMessage:
      "Эта форма {status}. Свяжитесь с {contact}, если у вас есть дополнительные вопросы.",
    contactFallbackUs: "нами",
    signature: "Подпись",
    signatureRequired: "Требуется подпись",
    signatureDraw: "Нарисовать",
    signatureType: "Ввести",
    signatureClear: "Очистить",
    signatureEditingDisabled: "Редактирование подписи отключено",
    digitalSignatureCalloutBody:
      "При выборе {action} вы будете перенаправлены на защищённую страницу {provider} для добавления подписи.",
    continueLabel: "Продолжить",
    signatureModalRedirect:
      "Для этой формы требуется подпись, выберите {action}, чтобы перейти на защищённую страницу подписания",
    signatureModalContact:
      "Для этой формы требуется подпись. Свяжитесь с {contact}, если у вас есть дополнительные вопросы.",
  },
};

export const getUITranslation = (
  key: UITranslationKey,
  language?: string,
): string => {
  const lang = (
    language && language in uiTranslations ? language : "en"
  ) as keyof typeof uiTranslations;
  return uiTranslations[lang][key];
};

// split around {marker} so a React node can sit in the translation's word order
// missing marker returns the whole string as the leading fragment
export const splitUITranslation = (
  key: UITranslationKey,
  marker: string,
  language?: string,
  replacements?: Record<string, string>,
): [string, string] => {
  const template = Object.entries(replacements || {}).reduce(
    (acc, [name, value]) => acc.replace(`{${name}}`, value),
    getUITranslation(key, language),
  );
  const token = `{${marker}}`;
  const index = template.indexOf(token);

  return index === -1
    ? [template, ""]
    : [template.slice(0, index), template.slice(index + token.length)];
};

export const validationKeys = {
  requiredQuestion: "requiredQuestion",
  pleaseEnterValidResponse: "pleaseEnterValidResponse",
  pleaseEnterValidDate: "pleaseEnterValidDate",
  pleaseEnterValidEmail: "pleaseEnterValidEmail",
  filesFailedToUpload: "filesFailedToUpload",
  filesStillUploading: "filesStillUploading",
  pleaseEnterValidPhone: "pleaseEnterValidPhone",
  appointmentMustBeCompleted: "appointmentMustBeCompleted",
  paymentMustBeCompleted: "paymentMustBeCompleted",
  pleaseSelectOptionsThatApply: "pleaseSelectOptionsThatApply",
  pleaseMakeASelection: "pleaseMakeASelection",
} as const;

export const getValidationMessage = (
  messageKey: string | undefined,
  language?: string,
): string => {
  if (!messageKey)
    return getUITranslation(validationKeys.pleaseEnterValidResponse, language);
  return getUITranslation(messageKey as keyof typeof validationKeys, language);
};

type FileUploadLimitTemplates = Record<SupportedLanguageKey | "en", string>;

const fileUploadLimitTemplates: FileUploadLimitTemplates = {
  en: "Max {maxFiles} files; {fileTypes}Max {sizeLimit} MB each.",
  ar: "الحد الأقصى {maxFiles} ملفات؛ {fileTypes}الحد الأقصى {sizeLimit} ميجابايت لكل ملف.",
  zh: "最多 {maxFiles} 个文件；{fileTypes}每个文件最多 {sizeLimit} MB。",
  fr: "Maximum {maxFiles} fichiers ; {fileTypes}Maximum {sizeLimit} Mo chacun.",
  de: "Maximal {maxFiles} Dateien; {fileTypes}Maximal {sizeLimit} MB pro Datei.",
  it: "Massimo {maxFiles} file; {fileTypes}Massimo {sizeLimit} MB ciascuno.",
  ja: "最大 {maxFiles} ファイル；{fileTypes}各ファイル最大 {sizeLimit} MB。",
  ko: "최대 {maxFiles}개 파일; {fileTypes}파일당 최대 {sizeLimit} MB.",
  pl: "Maksymalnie {maxFiles} plików; {fileTypes}Maksymalnie {sizeLimit} MB każdy.",
  pt: "Máximo {maxFiles} arquivos; {fileTypes}Máximo {sizeLimit} MB cada.",
  ru: "Максимум {maxFiles} файлов; {fileTypes}Максимум {sizeLimit} МБ каждый.",
  es: "Máximo {maxFiles} archivos; {fileTypes}Máximo {sizeLimit} MB cada uno.",
  vi: "Tối đa {maxFiles} tệp; {fileTypes}Tối đa {sizeLimit} MB mỗi tệp.",
};

export type FileUploadLimitParams = {
  maxFiles: number;
  fileTypeNames: string[];
  sizeLimit: number;
};

const buildFileTypesInfo = (names: string[], andWord: string): string => {
  if (!names.length) return "";
  const last = names.pop() || "";
  const info =
    names.length > 0 ? `${names.join(", ")} ${andWord} ${last}; ` : `${last}; `;
  return info.charAt(0).toLocaleUpperCase() + info.slice(1);
};

export const buildFileUpdateSizeLimitText = (
  params: FileUploadLimitParams,
  language?: string,
): string => {
  const lang = (
    language && language in fileUploadLimitTemplates ? language : "en"
  ) as keyof typeof fileUploadLimitTemplates;
  const andWord = getUITranslation("and", language);
  const fileTypes = buildFileTypesInfo([...params.fileTypeNames], andWord);
  const template = fileUploadLimitTemplates[lang];
  return template
    .replace("{maxFiles}", String(params.maxFiles))
    .replace("{fileTypes}", fileTypes)
    .replace("{sizeLimit}", String(params.sizeLimit));
};

type FileUploadPromptTemplates = Record<
  SupportedLanguageKey | "en",
  {
    touch: { action: string; instruction: string };
    desktop: { action: string; instruction: string };
  }
>;

const fileUploadPromptTemplates: FileUploadPromptTemplates = {
  en: {
    touch: { action: "Tap", instruction: "to upload {file}" },
    desktop: { action: "Browse", instruction: "or drag {file} here" },
  },
  ar: {
    touch: { action: "انقر", instruction: "لتحميل {file}" },
    desktop: { action: "تصفح", instruction: "أو اسحب {file} هنا" },
  },
  zh: {
    touch: { action: "点击", instruction: "上传{file}" },
    desktop: { action: "浏览", instruction: "或将{file}拖到此处" },
  },
  fr: {
    touch: { action: "Appuyez", instruction: "pour télécharger {file}" },
    desktop: { action: "Parcourir", instruction: "ou glisser {file} ici" },
  },
  de: {
    touch: { action: "Tippen", instruction: "zum Hochladen von {file}" },
    desktop: {
      action: "Durchsuchen",
      instruction: "oder {file} hierher ziehen",
    },
  },
  it: {
    touch: { action: "Tocca", instruction: "per caricare {file}" },
    desktop: { action: "Sfoglia", instruction: "o trascina {file} qui" },
  },
  ja: {
    touch: { action: "タップ", instruction: "{file}をアップロード" },
    desktop: { action: "参照", instruction: "または{file}をここにドラッグ" },
  },
  ko: {
    touch: { action: "탭", instruction: "{file} 업로드" },
    desktop: {
      action: "찾아보기",
      instruction: "또는 {file}을(를) 여기로 끌어오기",
    },
  },
  pl: {
    touch: { action: "Dotknij", instruction: "aby przesłać {file}" },
    desktop: {
      action: "Przeglądaj",
      instruction: "lub przeciągnij {file} tutaj",
    },
  },
  pt: {
    touch: { action: "Toque", instruction: "para carregar {file}" },
    desktop: { action: "Procurar", instruction: "ou arrastar {file} aqui" },
  },
  es: {
    touch: { action: "Toca", instruction: "para subir {file}" },
    desktop: { action: "Examinar", instruction: "o arrastrar {file} aquí" },
  },
  ru: {
    touch: { action: "Нажмите", instruction: "чтобы загрузить {file}" },
    desktop: {
      action: "Обзор",
      instruction: "или перетащите {file} сюда",
    },
  },
  vi: {
    touch: { action: "Nhấn", instruction: "để tải {file} lên" },
    desktop: { action: "Duyệt", instruction: "hoặc kéo {file} vào đây" },
  },
};

export type FileUploadPrompt = {
  touch: { action: string; instruction: string };
  desktop: { action: string; instruction: string };
};

export const buildFileUploadPrompt = (
  isMultiFile: boolean,
  language?: string,
): FileUploadPrompt => {
  const lang = (
    language && language in fileUploadPromptTemplates ? language : "en"
  ) as keyof typeof fileUploadPromptTemplates;
  const fileWord = getUITranslation(isMultiFile ? "files" : "file", language);
  const templates = fileUploadPromptTemplates[lang];

  const replaceFile = (s: string) => s.replace("{file}", fileWord);

  return {
    touch: {
      action: templates.touch.action,
      instruction: replaceFile(templates.touch.instruction),
    },
    desktop: {
      action: templates.desktop.action,
      instruction: replaceFile(templates.desktop.instruction),
    },
  };
};

type TranslatedMonthsShort = Record<SupportedLanguageKey | "en", string[]>;

const translatedMonthsShort: TranslatedMonthsShort = {
  en: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
  ar: [
    "يناير",
    "فبراير",
    "مارس",
    "أبريل",
    "مايو",
    "يونيو",
    "يوليو",
    "أغسطس",
    "سبتمبر",
    "أكتوبر",
    "نوفمبر",
    "ديسمبر",
  ],
  zh: [
    "1月",
    "2月",
    "3月",
    "4月",
    "5月",
    "6月",
    "7月",
    "8月",
    "9月",
    "10月",
    "11月",
    "12月",
  ],
  fr: [
    "janv.",
    "févr.",
    "mars",
    "avr.",
    "mai",
    "juin",
    "juil.",
    "août",
    "sept.",
    "oct.",
    "nov.",
    "déc.",
  ],
  de: [
    "Jan.",
    "Feb.",
    "März",
    "Apr.",
    "Mai",
    "Juni",
    "Juli",
    "Aug.",
    "Sep.",
    "Okt.",
    "Nov.",
    "Dez.",
  ],
  it: [
    "gen",
    "feb",
    "mar",
    "apr",
    "mag",
    "giu",
    "lug",
    "ago",
    "set",
    "ott",
    "nov",
    "dic",
  ],
  ja: [
    "1月",
    "2月",
    "3月",
    "4月",
    "5月",
    "6月",
    "7月",
    "8月",
    "9月",
    "10月",
    "11月",
    "12月",
  ],
  ko: [
    "1월",
    "2월",
    "3월",
    "4월",
    "5월",
    "6월",
    "7월",
    "8월",
    "9월",
    "10월",
    "11월",
    "12월",
  ],
  pl: [
    "sty",
    "lut",
    "mar",
    "kwi",
    "maj",
    "cze",
    "lip",
    "sie",
    "wrz",
    "paź",
    "lis",
    "gru",
  ],
  pt: [
    "jan",
    "fev",
    "mar",
    "abr",
    "mai",
    "jun",
    "jul",
    "ago",
    "set",
    "out",
    "nov",
    "dez",
  ],
  es: [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ],
  ru: [
    "янв",
    "фев",
    "мар",
    "апр",
    "май",
    "июн",
    "июл",
    "авг",
    "сен",
    "окт",
    "ноя",
    "дек",
  ],
  vi: [
    "Th1",
    "Th2",
    "Th3",
    "Th4",
    "Th5",
    "Th6",
    "Th7",
    "Th8",
    "Th9",
    "Th10",
    "Th11",
    "Th12",
  ],
};

export const getTranslatedMonthsShort = (language?: string): string[] => {
  const lang = (
    language && language in translatedMonthsShort ? language : "en"
  ) as keyof typeof translatedMonthsShort;
  return translatedMonthsShort[lang];
};

type TranslatedWeekDaysShort = Record<
  SupportedLanguageKey | "en",
  [string, string, string, string, string, string, string]
>;

const translatedWeekDaysShort: TranslatedWeekDaysShort = {
  en: ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"],
  ar: ["اث", "ثل", "أر", "خم", "جم", "سب", "أح"],
  zh: ["一", "二", "三", "四", "五", "六", "日"],
  fr: ["lu", "ma", "me", "je", "ve", "sa", "di"],
  de: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
  it: ["lu", "ma", "me", "gi", "ve", "sa", "do"],
  ja: ["月", "火", "水", "木", "金", "土", "日"],
  ko: ["월", "화", "수", "목", "금", "토", "일"],
  pl: ["pn", "wt", "śr", "cz", "pt", "sb", "nd"],
  pt: ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"],
  ru: ["пн", "вт", "ср", "чт", "пт", "сб", "вс"],
  es: ["lu", "ma", "mi", "ju", "vi", "sá", "do"],
  vi: ["T2", "T3", "T4", "T5", "T6", "T7", "CN"],
};

export const getTranslatedWeekDaysShort = (
  language?: string,
): [string, string, string, string, string, string, string] => {
  const lang = (
    language && language in translatedWeekDaysShort ? language : "en"
  ) as keyof typeof translatedWeekDaysShort;
  return translatedWeekDaysShort[lang];
};

export const getTranslatedDurationUnit = (
  duration: number,
  durationUnit: string | null | undefined,
  language?: string,
): string => {
  if (!durationUnit) return "";
  if (durationUnit === "hours") {
    return duration === 1
      ? getUITranslation("hour", language)
      : getUITranslation("hours", language);
  }
  return duration === 1
    ? getUITranslation("minute", language)
    : getUITranslation("minutes", language);
};

export const buildAppointmentSelectedMessage = (
  date: string,
  time: string,
  language?: string,
): string => {
  const template = getUITranslation("appointmentSelectedMessage", language);
  return template.replace("{date}", date).replace("{time}", time);
};

export const buildErrorSummaryHeading = (
  count: number,
  language?: string,
): string => {
  const template = getUITranslation("errorSummaryHeading", language);
  return template.replace("{count}", String(count));
};
